import { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ChevronLeft, Calendar, Trash2, Printer, Save, Edit, Zap, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { saveEventSetup, getMyToolsData, deleteToolItem, getBmsEvents, GetMyToolsDataOutputType, GetBmsEventsOutputType } from 'zite-endpoints-sdk';

type Saved = GetMyToolsDataOutputType['eventSetups'][0];
type BmsEvent = GetBmsEventsOutputType['events'][0];
type View = 'list' | 'form' | 'output';

type EventFormData = {
  eventName: string; eventDate: string; clientName: string; location: string; guestCount: string;
  eventType: string; startTime: string; endTime: string;
  menu: string; drinksPackage: string; preOrders: string; allergens: string;
  roomSetup: string; equipment: string; staffing: string;
  paymentNotes: string; depositPaid: string; extraNotes: string;
};

const defaultForm: EventFormData = {
  eventName: '', eventDate: new Date().toISOString().split('T')[0], clientName: '', location: '', guestCount: '',
  eventType: 'Private Hire', startTime: '18:00', endTime: '23:00',
  menu: '', drinksPackage: '', preOrders: '', allergens: '',
  roomSetup: '', equipment: '', staffing: '',
  paymentNotes: '', depositPaid: '', extraNotes: '',
};

function EventSetupOutputDoc({ data, onEdit, onSave, isSaving }: { data: EventFormData; onEdit: () => void; onSave: () => void; isSaving: boolean }) {
  const dateStr = data.eventDate ? format(parseISO(data.eventDate), 'EEEE do MMMM yyyy') : '—';
  const setupChecklist = [
    'Room layout confirmed and in place', 'Tables set and dressed', 'Bar stocked for event',
    'Pre-orders prepared / confirmed', 'Run sheet printed and shared with team',
    'PA / AV equipment checked', 'Lighting set', 'Signage / name cards in place',
    ...(data.allergens ? ['Allergen info shared with kitchen ⚠️'] : []),
    ...(data.extraNotes ? ['Special setup notes actioned'] : []),
  ];
  const clearDown = [
    'All glasses collected and washed', 'Tables cleared and wiped', 'Room returned to standard layout',
    'Bar area cleaned and restocked', 'Deposits / final payment confirmed',
    'Damage / incidents logged if applicable', 'Client thanked and feedback noted',
  ];
  return (
    <div>
      <div className="flex gap-2 mb-4 no-print">
        <Button variant="outline" size="sm" onClick={onEdit}><Edit className="w-4 h-4 mr-1.5" /> Edit</Button>
        <Button variant="outline" size="sm" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1.5" /> Print</Button>
        <Button size="sm" onClick={onSave} disabled={isSaving}><Save className="w-4 h-4 mr-1.5" />{isSaving ? 'Saving...' : 'Save'}</Button>
      </div>
      <div className="print-page bg-card border border-border rounded-xl p-6 md:p-8 space-y-5 max-w-4xl">
        <div className="border-b-2 border-foreground pb-4">
          <h1 className="text-2xl font-bold tracking-widest uppercase">The Rattlebone</h1>
          <h2 className="text-base font-semibold text-muted-foreground uppercase tracking-wide">Event Setup Sheet</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Event</span>{data.eventName || '—'}</div>
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Date</span>{dateStr}</div>
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Client</span>{data.clientName || '—'}</div>
            <div><span className="font-semibold block text-xs uppercase text-muted-foreground">Printed</span>{format(new Date(), 'HH:mm dd/MM/yy')}</div>
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wide border-b border-border pb-1 mb-2">Event Details</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-muted-foreground block">Type</span>{data.eventType}</div>
            <div><span className="text-muted-foreground block">Location</span>{data.location || '—'}</div>
            <div><span className="text-muted-foreground block">Guests</span>{data.guestCount || '—'}</div>
            <div><span className="text-muted-foreground block">Time</span>{data.startTime} – {data.endTime}</div>
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wide border-b border-border pb-1 mb-2">Food & Drinks</h3>
          <div className="space-y-1 text-sm">
            {data.menu && <p><span className="text-muted-foreground">Menu:</span> {data.menu}</p>}
            {data.drinksPackage && <p><span className="text-muted-foreground">Drinks package:</span> {data.drinksPackage}</p>}
            {data.preOrders && <p><span className="text-muted-foreground">Pre-orders:</span> {data.preOrders}</p>}
            {data.allergens && <p className="text-destructive font-medium">⚠️ Allergens: {data.allergens}</p>}
          </div>
        </div>
        {(data.roomSetup || data.equipment || data.staffing) && (
          <div>
            <h3 className="font-semibold text-xs uppercase tracking-wide border-b border-border pb-1 mb-2">Setup Requirements</h3>
            <div className="space-y-1 text-sm">
              {data.roomSetup && <p><span className="text-muted-foreground">Room setup:</span> {data.roomSetup}</p>}
              {data.equipment && <p><span className="text-muted-foreground">Equipment:</span> {data.equipment}</p>}
              {data.staffing && <p><span className="text-muted-foreground">Staffing:</span> {data.staffing}</p>}
            </div>
          </div>
        )}
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wide border-b border-border pb-1 mb-3">Setup Checklist</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {setupChecklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-foreground/40 rounded shrink-0" /><span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wide border-b border-border pb-1 mb-3">Clear-Down Checklist</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {clearDown.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 border-2 border-foreground/40 rounded shrink-0" /><span>{item}</span>
              </div>
            ))}
          </div>
        </div>
        {(data.paymentNotes || data.depositPaid || data.extraNotes) && (
          <div>
            <h3 className="font-semibold text-xs uppercase tracking-wide border-b border-border pb-1 mb-2">Notes</h3>
            <div className="space-y-1 text-sm">
              {data.depositPaid && <p><span className="text-muted-foreground">Deposit:</span> {data.depositPaid}</p>}
              {data.paymentNotes && <p><span className="text-muted-foreground">Payment:</span> {data.paymentNotes}</p>}
              {data.extraNotes && <p className="whitespace-pre-wrap">{data.extraNotes}</p>}
            </div>
          </div>
        )}
        <div className="border-t-2 border-foreground pt-4 grid grid-cols-2 gap-8 text-sm">
          <div><p className="font-medium">Setup confirmed by: _______________________</p><p className="mt-3 text-xs text-muted-foreground">Signature / Time</p></div>
          <div><p className="font-medium">Manager sign-off: _______________________</p><p className="mt-3 text-xs text-muted-foreground">Signature / Time</p></div>
        </div>
      </div>
    </div>
  );
}

export default function EventSetup() {
  const { user } = useAuth();
  const [view, setView] = useState<View>('list');
  const [formData, setFormData] = useState<EventFormData>({ ...defaultForm });
  const [savedId, setSavedId] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [bmsEvents, setBmsEvents] = useState<BmsEvent[]>([]);
  const [bmsConnected, setBmsConnected] = useState(false);

  useEffect(() => {
    if (!user) return;
    getBmsEvents({ daysAhead: 30 })
      .then(d => {
        setBmsConnected(d.connected);
        const now = new Date();
        setBmsEvents(d.events.filter(e => !e.date || new Date(e.date) >= now).slice(0, 10));
      })
      .catch(() => {});
  }, [user]);

  const populateFromBmsEvent = (event: BmsEvent) => {
    setFormData({
      eventName: event.name || '',
      eventDate: event.date || new Date().toISOString().split('T')[0],
      clientName: event.clientName || '',
      location: event.location || '',
      guestCount: event.guestCount || '',
      eventType: event.eventType || 'Private Hire',
      startTime: event.startTime || '18:00',
      endTime: event.endTime || '23:00',
      menu: event.menu || '',
      drinksPackage: event.drinksPackage || '',
      preOrders: event.preOrders || '',
      allergens: event.allergens || '',
      roomSetup: '',
      equipment: '',
      staffing: '',
      paymentNotes: '',
      depositPaid: event.depositPaid || '',
      extraNotes: event.notes || '',
    });
    setSavedId(null);
    setView('form');
    toast.success(`Imported from BMS: ${event.name}`);
  };

  const loadSaved = useCallback(() => {
    if (!user) return;
    getMyToolsData({}).then(d => setSaved(d.eventSetups.filter(s => s.status !== 'Archived')));
  }, [user]);

  useEffect(() => { loadSaved(); }, [loadSaved]);

  const fld = (key: keyof EventFormData) => ({
    value: formData[key], onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setFormData(p => ({ ...p, [key]: e.target.value })),
  });

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const title = `${formData.eventName || 'Event'} — ${formData.eventDate ? format(parseISO(formData.eventDate), 'do MMM yyyy') : ''}`;
      const res = await saveEventSetup({ id: savedId || undefined, title, eventDate: formData.eventDate, formData: JSON.stringify(formData), status: 'Final' });
      if (!savedId) setSavedId(res.id);
      toast.success('Event setup sheet saved!');
      loadSaved();
    } catch { toast.error('Failed to save'); } finally { setIsSaving(false); }
  };

  if (view === 'output') return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setView('list')} className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold">Event Setup Sheet</h1>
      </div>
      <EventSetupOutputDoc data={formData} onEdit={() => setView('form')} onSave={handleSave} isSaving={isSaving} />
    </div>
  );

  if (view === 'form') return (
    <div className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => setView('list')} className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="text-2xl font-bold">{savedId ? 'Edit Event Setup' : 'New Event Setup'}</h1>
      </div>
      <Card className="p-6 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Label>Event Name</Label><Input className="mt-1" {...fld('eventName')} placeholder="e.g. Smith Wedding Reception" /></div>
          <div><Label>Date</Label><Input className="mt-1" type="date" {...fld('eventDate')} /></div>
          <div><Label>Event Type</Label>
            <Select value={formData.eventType} onValueChange={v => setFormData(p => ({...p, eventType: v}))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['Private Hire', 'Wedding', 'Birthday', 'Corporate', 'Wake', 'Charity', 'Live Music', 'Quiz Night', 'Other'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Client Name</Label><Input className="mt-1" {...fld('clientName')} placeholder="Contact name" /></div>
          <div><Label>Location / Area</Label><Input className="mt-1" {...fld('location')} placeholder="e.g. The Barn, Garden" /></div>
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div><Label>Guest Numbers</Label><Input className="mt-1" type="number" {...fld('guestCount')} placeholder="0" /></div>
          <div><Label>Start Time</Label><Input className="mt-1" type="time" {...fld('startTime')} /></div>
          <div><Label>End Time</Label><Input className="mt-1" type="time" {...fld('endTime')} /></div>
        </div>
        <div><Label>Menu / Food Package</Label><Textarea className="mt-1" {...fld('menu')} rows={2} placeholder="Set menu, canapés, bowl food, etc." /></div>
        <div><Label>Drinks Package</Label><Input className="mt-1" {...fld('drinksPackage')} placeholder="e.g. Arrival drinks + £20pp bar tab" /></div>
        <div><Label>Pre-Orders</Label><Textarea className="mt-1" {...fld('preOrders')} rows={2} placeholder="Any pre-ordered items" /></div>
        <div className="bg-destructive/5 border border-destructive/20 rounded-xl p-3"><Label className="text-destructive">⚠️ Allergens</Label><Textarea className="mt-1.5" {...fld('allergens')} rows={2} placeholder="IMPORTANT: list all allergen requirements" /></div>
        <div><Label>Room Setup Requirements</Label><Textarea className="mt-1" {...fld('roomSetup')} rows={2} placeholder="Table layout, chairs, decorations needed" /></div>
        <div><Label>Equipment Needed</Label><Input className="mt-1" {...fld('equipment')} placeholder="e.g. PA system, projector, microphone, dance floor" /></div>
        <div><Label>Staffing</Label><Input className="mt-1" {...fld('staffing')} placeholder="e.g. 2 bar staff, 3 FOH, 1 manager" /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Deposit Paid</Label><Input className="mt-1" {...fld('depositPaid')} placeholder="e.g. £500 on 01/05/26" /></div>
          <div><Label>Payment Notes</Label><Input className="mt-1" {...fld('paymentNotes')} placeholder="Balance due on the day" /></div>
        </div>
        <div><Label>Extra Notes</Label><Textarea className="mt-1" {...fld('extraNotes')} rows={3} placeholder="Anything else the team needs to know" /></div>
        <div className="flex gap-2 pt-2 border-t border-border">
          <Button variant="outline" onClick={() => setView('list')} className="flex-1">Cancel</Button>
          <Button onClick={() => setView('output')} className="flex-1">Preview & Generate →</Button>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/tools" className="text-muted-foreground hover:text-foreground"><ChevronLeft className="w-5 h-5" /></Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Event Setup Builder</h1>
          <p className="text-sm text-muted-foreground">Build printable event setup sheets, run sheets and staff briefings</p>
        </div>
        <Button onClick={() => { setFormData({ ...defaultForm }); setSavedId(null); setView('form'); }}><Plus className="w-4 h-4 mr-1.5" /> New Event Setup</Button>
      </div>
      {/* BMS Live Events Panel */}
      {bmsConnected && bmsEvents.length > 0 && (
        <Card className="mb-6 border-primary/30 overflow-hidden">
          <div className="px-4 py-3 bg-primary/5 border-b border-border flex items-center gap-2">
            <Zap className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Upcoming Bookings from BMS</span>
            <span className="text-xs text-muted-foreground ml-auto">Live data · click to import</span>
          </div>
          <div className="divide-y divide-border">
            {bmsEvents.map(event => (
              <div key={event.id} className="px-4 py-3 flex items-center gap-3 hover:bg-muted/30 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{event.name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5">
                    {event.date && <span className="text-xs text-muted-foreground">{format(parseISO(event.date), 'EEE do MMM yyyy')}</span>}
                    {event.startTime && <span className="text-xs text-muted-foreground">{event.startTime}{event.endTime ? ` – ${event.endTime}` : ''}</span>}
                    {event.guestCount && <span className="text-xs text-muted-foreground">{event.guestCount} guests</span>}
                    {event.clientName && <span className="text-xs text-muted-foreground">· {event.clientName}</span>}
                    {event.allergens && <span className="text-xs text-destructive font-medium">⚠️ Allergens</span>}
                    {event.depositPaid && <span className="text-xs text-green-600">✓ Deposit</span>}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => populateFromBmsEvent(event)} className="shrink-0">
                  Import →
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {!bmsConnected && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-muted/30 border border-dashed border-border rounded-xl text-xs text-muted-foreground">
          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          <span>Connect to BMS Hub to auto-import bookings and event details here. Contact your manager to configure the BMS bridge.</span>
        </div>
      )}

      {saved.length === 0 ? (
        <Card className="p-12 text-center">
          <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="font-semibold text-lg mb-1">No event setups yet</h3>
          <p className="text-muted-foreground text-sm mb-4">Create your first event setup sheet</p>
          <Button onClick={() => { setFormData({ ...defaultForm }); setSavedId(null); setView('form'); }}><Plus className="w-4 h-4 mr-1.5" /> Create Event Setup</Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {saved.map(s => (
            <Card key={s.id} className="p-4 flex items-center gap-4 hover:border-primary/40 transition-colors">
              <Calendar className="w-8 h-8 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{s.title}</p>
                <p className="text-xs text-muted-foreground">{s.eventDate ? format(parseISO(s.eventDate), 'EEE do MMM yyyy') : ''}</p>
              </div>
              <Badge variant={s.status === 'Final' ? 'default' : 'outline'} className="text-xs shrink-0">{s.status}</Badge>
              <div className="flex gap-1 shrink-0">
                <Button variant="outline" size="sm" onClick={() => { try { setFormData(JSON.parse(s.formData)); setSavedId(s.id); setView('form'); } catch { toast.error('Could not load'); } }}>Open</Button>
                <Button variant="ghost" size="sm" onClick={async () => { try { await deleteToolItem({ type: 'event', id: s.id, action: 'archive' }); toast.success('Archived'); loadSaved(); } catch { toast.error('Failed'); } }}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
